import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Button57 from '../components/Button57';

const { FiArrowRight, FiStar, FiMapPin, FiPhone } = FiIcons;

const Home = () => {
  const googleMapsUrl = "https://www.google.com/maps/place/Ganda+Sushi/@40.726154,-73.789221,17z/data=!3m1!4b1!4m6!3m5!1s0x89c26100ca7163ad:0x4b7fe7e097bf9f58!8m2!3d40.726154!4d-73.789221!16s%2Fg%2F1thsb8h7?entry=ttu&g_ep=EgoyMDI2MDEwNC4wIKXMDSoKLDEwMDc5MjA3MUgBUAM%3D";
  const phoneNumber = "(718) 380-1818";
  const telLink = "tel:+17183801818";
  
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 1.2, ease: "easeOut" }
  };

  return (
    <>
      <Helmet>
        <title>Ganda Sushi | Premium Korean & Japanese Dining in Queens, NY</title>
        <meta name="description" content="Experience the finest Korean and Japanese fusion at Ganda Sushi. Located in Jamaica, Queens, we offer artisan sushi, authentic Korean BBQ, and a luxury dining atmosphere." />
        <meta name="keywords" content="Ganda Sushi, Sushi Queens, Korean Food Jamaica NY, Asian Fusion Queens" />
        <link rel="canonical" href="https://gandasushi.com/" />
        <meta property="og:title" content="Ganda Sushi | Premium Korean & Japanese Dining" />
        <meta property="og:url" content="https://gandasushi.com/" />
      </Helmet>

      <main className="bg-luxury-cream text-luxury-dark overflow-hidden" aria-label="Home Page">
        {/* Hero Section */}
        <section className="relative h-screen flex items-center justify-center overflow-hidden" aria-labelledby="hero-heading">
          <div className="absolute inset-0 z-0">
            <div className="w-full h-full">
              <img 
                src="https://images.unsplash.com/photo-1579871494447-9811cf80d66c?auto=format&fit=crop&w=2000&q=80" 
                alt="Exquisite Sushi Platter Art" 
                className="w-full h-full object-cover brightness-75 shadow-inner"
              />
            </div>
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />
          </div>

          <div className="relative z-10 text-center px-6 max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.1 }}
              className="mb-6"
            >
              <span className="text-white tracking-[0.6em] uppercase text-xs font-bold drop-shadow-md">
                Queens' Finest Korean & Japanese Fusion
              </span>
            </motion.div>

            <motion.h1 
              id="hero-heading"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.5, delay: 0.3, ease: "easeOut" }}
              className="text-6xl md:text-9xl font-serif text-white mb-8 leading-tight drop-shadow-2xl"
            >
              Ganda Sushi
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="flex flex-col items-center gap-3 mb-12"
            >
              <a 
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-white/90 hover:text-luxury-gold transition-colors duration-300 text-sm tracking-[0.2em] uppercase font-light drop-shadow-md group"
                aria-label="Find us at 80-08 Surrey Pl, Jamaica, NY 11432"
              >
                <SafeIcon icon={FiMapPin} className="text-luxury-gold group-hover:scale-110 transition-transform" />
                <span>80-08 Surrey Pl, Jamaica, NY 11432</span>
              </a>
              <a 
                href={telLink}
                className="flex items-center gap-2 text-white/90 hover:text-luxury-gold transition-colors duration-300 text-sm tracking-[0.2em] uppercase font-light drop-shadow-md group"
                aria-label="Call Ganda Sushi at 718-380-1818"
              >
                <SafeIcon icon={FiPhone} className="text-luxury-gold group-hover:scale-110 transition-transform" />
                <span>{phoneNumber}</span>
              </a>
            </motion.div>

            <motion.nav 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.9 }}
              className="flex flex-col sm:flex-row justify-center items-center gap-6"
              aria-label="Hero Actions"
            >
              <Button57 
                to="/menu" 
                text="Order Now" 
                altText="View Menu"
                variant="light"
                className="min-w-[200px]"
              />
              <Button57 
                to="/about" 
                text="Our Story" 
                altText="Learn More"
                variant="light"
                className="min-w-[200px]"
              />
            </motion.nav>
          </div>
        </section>

        {/* Philosophy Intro */}
        <section className="py-32 bg-luxury-cream border-b border-luxury-gold/10" aria-labelledby="philosophy-heading">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <motion.div {...fadeInUp} className="max-w-3xl mx-auto">
              <span className="text-luxury-gold tracking-[0.4em] uppercase text-xs font-bold mb-6 block">Our Philosophy</span>
              <h2 id="philosophy-heading" className="text-4xl md:text-6xl font-serif mb-10 text-luxury-dark leading-snug">The Art of Balance</h2>
              <p className="text-luxury-dark/90 text-xl md:text-2xl leading-relaxed font-light italic">
                "We believe that dining is a multisensory journey. From the vibrant sizzle of Korean BBQ to the serene precision of our rolls, every plate tells a story of harmony."
              </p>
            </motion.div>
          </div>
        </section>

        {/* Featured Section */}
        <section className="py-32 bg-white" aria-labelledby="featured-heading">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
            <motion.div {...fadeInUp}>
              <h2 id="featured-heading" className="text-4xl md:text-5xl font-serif mb-8 text-luxury-dark">Exquisite Ingredients</h2>
              <p className="text-luxury-dark/80 text-lg leading-relaxed mb-12 font-light">
                We source only the highest grade seasonal fish and premium meats, 
                ensuring that every bite is a celebration of quality and freshness. 
                Our culinary team blends time-honored techniques with contemporary innovation.
              </p>
              <ul className="space-y-6 mb-12">
                {['Daily Fresh Fish Market Deliveries', 'Authentic Korean Spices & Marinades', 'Traditional Sushi Craftsmanship'].map((item, i) => (
                  <li key={i} className="flex items-center text-luxury-dark font-medium text-lg">
                    <SafeIcon icon={FiStar} className="text-luxury-gold mr-4 w-5 h-5" aria-hidden="true" /> {item}
                  </li>
                ))}
              </ul>
              <Button57 
                to="/menu" 
                text="View Selection" 
                altText="Explore Menu"
              />
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.2 }}
              className="relative aspect-square"
            >
              <img 
                src="https://images.unsplash.com/photo-1611143669185-af224c5e3252?auto=format&fit=crop&w=1200&q=80" 
                alt="Signature Sushi Platter showing artisan presentation" 
                className="w-full h-full object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-luxury-gold/10 -z-10 rounded-2xl" />
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-luxury-cream -z-10 border border-luxury-gold/20 rounded-2xl" />
            </motion.div>
          </div>
        </section>

        {/* Reservation CTA */}
        <section className="py-32 bg-luxury-cream text-center border-t border-luxury-gold/10" aria-labelledby="cta-heading">
          <motion.div {...fadeInUp} className="max-w-4xl mx-auto px-6">
            <h2 id="cta-heading" className="text-4xl md:text-7xl font-serif mb-10 text-luxury-dark">Begin the Journey</h2>
            <p className="text-luxury-dark/90 mb-12 text-xl font-light leading-relaxed">
              Experience the intersection of luxury and authenticity. <br className="hidden md:block"/>
              Reserve your table at Ganda Sushi today for an unforgettable evening.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-6">
              <Button57 
                href="tel:+17183801818" 
                text="Book Now" 
                altText="Call (718) 380-1818"
                className="min-w-[180px]"
              />
              <Button57 
                to="/contact" 
                text="Contact Us" 
                altText="Get in Touch"
                className="min-w-[180px]"
              />
            </div>
          </motion.div>
        </section>
      </main>
    </>
  );
};

export default Home;